// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { SDUIComponentRenderer } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/SDUIComponentRenderer/SDUIComponentRenderer";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { SectionLoggingEventParams } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/types";
import { SDUICarouselRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Carousel/SDUICarouselRenderer";
import { SDUISpacerRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Spacer/SDUISpacerRenderer";
import { ComponentRenderMap } from "@faire/retailer-visitor-shared/components/SDUI/types";
import { useSearchPageQueryParams } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageQueryParams";
import { useQuery_escape_hatch as useSearchRelatedQueries } from "@faire/web-api--source/endpoints/www/api/v3/layout/related-search-queries/post-hooks";
import { DisplayPageType } from "@faire/web-api--source/indigofair/data/DisplayPageType";
import { IGetSearchRelatedQueriesRequest } from "@faire/web-api--source/indigofair/data/IGetSearchRelatedQueriesRequest";
import React from "react";

const SECTION_LOGGING_EVENT_PARAMS = SectionLoggingEventParams.build({
  view_display_page: DisplayPageType.SEARCH_PAGE,
  view_instance_identifier: "",
  section_type: "search_related_query_carousel_section",
});

const componentRenderers: ComponentRenderMap = {
  carousel_component: {
    active: SDUICarouselRenderer,
  },
  spacer_component: {
    active: SDUISpacerRenderer,
  },
};

export const SearchRelatedSuggestionsCarousel: React.FC = () => {
  const {
    doQuerySpellCheck,
    zeroIndexedPageNumber,
    searchQuery: query,
  } = useSearchPageQueryParams();

  const shouldFetchRelatedSuggestionsCarousel =
    !!query && zeroIndexedPageNumber === 0;

  const { data, isPending, isError } = useSearchRelatedQueries(
    IGetSearchRelatedQueriesRequest.build({
      query,
      do_query_spell_check: doQuerySpellCheck,
      page_number: zeroIndexedPageNumber + 1,
    }),
    {
      // stale time is set to 30 seconds to match RSC payload cache time
      staleTime: 30 * 1000,
      enabled: shouldFetchRelatedSuggestionsCarousel,
    }
  );

  if (!shouldFetchRelatedSuggestionsCarousel || isPending || isError || !data) {
    return null;
  }

  const { component: carouselComponent, spacer_component: spacerComponent } =
    data;

  if (!data.component && !data.spacer_component) {
    return null;
  }

  // case for control group where BE returns the space component only
  if (spacerComponent && !carouselComponent) {
    return (
      <div
        data-test-id="search-related-queries-spacer"
        className="search-item-no-bottom-margin col-span-full"
      >
        <SDUIComponentRenderer
          component={spacerComponent}
          componentRenderers={componentRenderers}
          sectionLoggingParams={SECTION_LOGGING_EVENT_PARAMS}
          isPrimaryContent={false}
        />
      </div>
    );
  }

  // case for treatment group where BE returns both spacer and carousel components
  return (
    <div
      data-test-id="search-related-queries-carousel"
      className="col-span-full"
    >
      {spacerComponent ? (
        <SDUIComponentRenderer
          component={spacerComponent}
          componentRenderers={componentRenderers}
          sectionLoggingParams={SECTION_LOGGING_EVENT_PARAMS}
          isPrimaryContent={false}
        />
      ) : null}
      {carouselComponent ? (
        <SDUIComponentRenderer
          component={carouselComponent}
          componentRenderers={componentRenderers}
          sectionLoggingParams={SECTION_LOGGING_EVENT_PARAMS}
          isPrimaryContent={false}
        />
      ) : null}
    </div>
  );
};
